import { useDeferredValue, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import TabPanel from './components/TabPanel';

export interface TabView {
  title: string | React.ReactNode;
  content: React.ReactNode;
}

interface Props {
  currentTabIndex: number;
  setTabIndex: (index: number) => void;
  tabs: TabView[];
}

export const TabbedView = ({ currentTabIndex, setTabIndex, tabs }: Props) => {
  // The header and the panels update on two clocks (Asana 1219915395102217).
  // A click selects the tab header at once (urgent local state, before the
  // route changes), so the label and the indicator move on the next frame.
  // The panels follow the route through a deferred value, so a first visit's
  // render of a 100-row list happens in the background without holding back
  // that frame; until it lands the outgoing panel stays up, dimmed.
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  useEffect(() => {
    setClickedIndex(null);
  }, [currentTabIndex]);
  const selectedIndex = clickedIndex ?? currentTabIndex;
  const panelIndex = useDeferredValue(currentTabIndex);
  const pending = selectedIndex !== panelIndex;

  const onTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setClickedIndex(newValue);
    setTabIndex(newValue);
  };

  // A tab mounts on its first visit and then stays mounted, hidden and inert,
  // so switching back costs nothing. The set only grows, and adding the panel
  // index is idempotent, so a ref is enough. Keyed on the deferred index so the
  // mount itself is part of the deferred render, never the urgent one.
  const visited = useRef(new Set<number>());
  visited.current.add(panelIndex);
  return (
    <>
      <Box
        sx={{
          borderBottom: 1,
          paddingBottom: 0,
          marginBottom: '12px',
          borderColor: '#2E2E2E',
        }}
      >
        <Tabs
          value={selectedIndex}
          onChange={onTabChange}
          sx={({ colors }) => ({
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: '400',
              color: colors.white,
              opacity: 0.5,
              letterSpacing: '-0.02px',
              minWidth: 'auto',
              paddingLeft: '0px',
              marginRight: '20px',
              paddingBottom: '10px',
              '&.Mui-selected': {
                color: colors.white,
                opacity: 1,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.violetBlue,
              height: '2px',
              marginTop: 0,
              paddingTop: 0,
            },
          })}
        >
          {tabs.map((tab, index) => (
            <Tab key={`tab-${index}`} label={tab.title} />
          ))}
        </Tabs>
      </Box>
      <div className={`tab-panels${pending ? ' tab-panels--pending' : ''}`} data-testid="tab-panels">
        {tabs.map((tab, index) =>
          visited.current.has(index) ? (
            <TabPanel
              key={`tab-panel-${index}`}
              index={index}
              activeIndex={currentTabIndex}
              shownIndex={panelIndex}
            >
              {tab.content}
            </TabPanel>
          ) : null,
        )}
      </div>
    </>
  );
};
